import React from "react"
import { SelectBoxOption } from "../../../interfaces"
import { Stack } from "@mui/system"
import { Box, Button, Typography } from "@mui/material"
import { NounSelectBoxWithoutSeach } from "../../nounSelectBox/withoutSearch"
import { FilterStateInterface } from "../withSelect"
import copy from "fast-copy"
import { useLazyGetNounsQuery } from "../../../redux/services"
import { GridCellParams, GridEventListener, GridRenderCellParams, MuiEvent } from "@mui/x-data-grid-premium"
import { StyledDataGrid } from "../../../screens/workbook/actions/actionTable"
import { InternatlServerError, LoadingSection, NotFoundSection } from "../../integrationHelper"

const FilterNounWithListView: React.FC<{
    showNoun?: boolean,
    showNounCard?: boolean,
    appliedFilters: FilterStateInterface,
    onApply: (state: FilterStateInterface) => void,
    onCancel: () => void
}> = ({
    showNoun, appliedFilters, onApply, onCancel, showNounCard,
}) => {
        const [refetch, { isFetching, isLoading, isError, isSuccess, data: allData = { data: [] } }] = useLazyGetNounsQuery({});

        const [state, setState] = React.useState<FilterStateInterface>({ ...appliedFilters });

        const loading = isFetching || isLoading;

        const giveMeNouns = () => {
            if (isSuccess && allData?.data?.length) {
                let newData = copy(allData?.data);
                return newData.map((n) => ({ ...n, value: n._id, label: n.noun_name }));
            }
            return []
        }

        const ROWS = giveMeNouns();

        const handleRowClick: GridEventListener<'rowClick'> = (params) => {
            setState({ ...state, noun: params.row })
        };

        let Columns = React.useMemo(() => {
            return [
                {
                    field: "attachments",
                    headerName: "Noun Image",
                    width: 150,
                    renderCell: (params: GridRenderCellParams) => (
                        <Box>
                            <img
                                height={"50px"}
                                width={"50px"}
                                style={{ objectFit: "contain" }}
                                src={
                                    params?.row?.attachments
                                        ? params?.row?.attachments?.at(-1)?.url
                                        : `/imageNotFound.jpg`
                                }
                                alt={params?.row?.attachments?.at(-1)?.file_name ?? ""}
                            />
                        </Box>
                    ),
                    disableColumnMenu: true,
                    sortable: false,
                },
                {
                    field: "noun_name",
                    headerName: "Noun Name",
                    width: 200,
                    renderCell: (params: GridRenderCellParams) => {
                        if (params.rowNode.type === "group") {
                            return params.value;
                        }
                        return <Typography variant="body2">{params.row?.noun_name}</Typography>;
                    },
                },
                {
                    field: "group_name",
                    headerName: "Group Name",
                    width: 200,
                    renderCell: (params: GridRenderCellParams) => {
                        if (params.rowNode.type === "group") {
                            return params.value;
                        }
                        return <Typography variant="body2">{params.row?.group_name}</Typography>;
                    },
                },
            ];
            // eslint-disable-next-line
        }, [status])

        React.useEffect(() => {
            setState({ ...appliedFilters })
        }, [appliedFilters]);

        // eslint-disable-next-line
        React.useEffect(() => { refetch({ page: null, pageSize: null }) }, []);

        const COLUMNS = React.useMemo(() => Columns, [Columns]);

        return (
            <Box>
                <Stack spacing={2} sx={{ p: 2, height: "calc(100vh - 210px)", overflow: "hidden", pb: 3, pt: 0, position: "relative" }}>
                    {/* Noun Select Box */}
                    {showNoun && <Box sx={{ width: "100%" }}>
                        <NounSelectBoxWithoutSeach label="Noun" value={state.noun} onChange={(noun: SelectBoxOption) => setState({ ...state, noun })} uom_id={""} />
                    </Box>}

                    {/* Noun with images */}
                    {showNounCard && <Box
                        sx={{
                            flexGrow: 1,
                            width: "100%",
                            overflow: "auto"
                        }}>
                        {loading && <LoadingSection message={'Fetching Nouns...'} />}
                        {!loading && isError && <InternatlServerError message={'Oops! Something Went Wrong, Unable to fetch Nouns'} onRetry={() => refetch({ page: null, pageSize: null })} />}
                        {!loading && !isError && allData?.data?.length <= 0 && <NotFoundSection message='You are yet to create nouns.' />}
                        {!loading && isSuccess && allData?.data?.length > 0 &&
                            <StyledDataGrid
                                id="noun-list-view"
                                sx={{
                                    '.MuiDataGrid-row': {
                                        cursor: "pointer"
                                    },
                                    '.MuiDataGrid-cell:focus': {
                                        outlineColor: "transparent !important"
                                    },
                                    '.MuiDataGrid-virtualScroller': {
                                        overflow: 'auto !important',
                                        height: "calc(100vh - 440px) !important", // Adjust height for header and select box
                                    },
                                    '.MuiDataGrid-columnHeaders': {
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1000,
                                        backgroundColor: 'white',
                                    },

                                }}
                                rows={ROWS}
                                columns={COLUMNS}
                                loading={loading}
                                getRowId={(row) => row._id}
                                onRowClick={handleRowClick}
                                onCellClick={(params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
                                    event.defaultMuiPrevented = true;
                                }}
                                pagination={false}
                                getRowClassName={(params) => {
                                    let id = params?.row?._id
                                    return id === state.noun?._id ? `highlight-table-row` : ""
                                }}
                            />}
                    </Box>}

                    {/* Apply Cancel Button */}
                </Stack>
                {!isLoading && isSuccess && allData?.data?.length > 0 &&
                    <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                            justifyContent: "end",
                            background: (theme) => theme.palette.background.paper,
                            padding: 2,
                            pb: 0
                        }}>
                        <Button variant="contained" onClick={() => onApply(state)}>Apply</Button>
                        <Button variant="outlined" onClick={() => {
                            onCancel();
                            setState({});
                        }} >Reset</Button>
                    </Stack>
                }
            </Box>
        );
    }

export default FilterNounWithListView;